import React, { useEffect, useState } from 'react'
import { FormCheckboxField, FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { NameDto } from 'api/Common/Types'

import { SelectVersion } from '../SelectVersion'
import { PlatformVersion } from './PlatformVersions'

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  data?: PlatformVersion | null
  handleEdit(values: PlatformVersionValues): Promise<void>
}

const defaultData = {
  updateRequired: false,
}

export const platformVersions = [
  { id: 1, name: 'Android' },
  { id: 2, name: 'iOS' },
  { id: 3, name: 'Web' },
]

export type PlatformVersionValues = Partial<PlatformVersion>

export const PlatformVersionModal: React.FC<OuterProps> = ({
  handleEdit,
  data,
  isOpen,
  onClose,
}) => {
  const [platformVersion, setPlatformVersion] = useState<PlatformVersionValues>(data ?? defaultData)
  const [versionModal, setVersionModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setPlatformVersion(data ?? defaultData)
    setVersionModal(false)
  }, [data, isOpen])

  const changeVersion = (newVersion: NameDto<string>) => {
    setPlatformVersion((prev) => ({ ...prev, version: newVersion }))
    setVersionModal(false)
  }

  const submit = async () => {
    if (!platformVersion.platform || !platformVersion.version?.id || isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      await handleEdit(platformVersion)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{data?.id ? 'Edit Platform Version' : 'Add Platform Version'}</DialogTitle>
          </DialogHeader>
          <div className='flex flex-col gap-4'>
            <FormReadonlyField
              label='Version'
              value={platformVersion?.version?.name ?? ''}
              rightAddon={
                <Button
                  size='sm'
                  type='button'
                  variant='outline'
                  onClick={() => setVersionModal(true)}
                >
                  Choose
                </Button>
              }
            />
            <FormSelectField
              label='Platform'
              options={platformVersions}
              value={platformVersion?.platform?.id}
              onChange={(value) => {
                const platform = platformVersions.find((item) => item.id === Number(value))

                if (!platform) {
                  return
                }

                setPlatformVersion((prev) => ({ ...prev, platform }))
              }}
            />
            <FormCheckboxField
              label='Forced Update'
              value={Boolean(platformVersion.updateRequired)}
              onChange={(checked) =>
                setPlatformVersion((prev) => ({ ...prev, updateRequired: checked }))
              }
            />
          </div>
          <DialogFooter>
            <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
              Cancel
            </Button>
            <Button
              type='button'
              disabled={isSubmitting || !platformVersion.platform || !platformVersion.version?.id}
              onClick={submit}
            >
              {data?.id ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={versionModal} onOpenChange={setVersionModal}>
        <DialogContent className='flex max-h-[85vh] max-w-[1300px] flex-col overflow-hidden p-0'>
          <DialogHeader className='shrink-0 px-6 pt-6'>
            <DialogTitle>Select Version</DialogTitle>
          </DialogHeader>
          <div className='min-h-0 flex-1 overflow-y-auto px-6 pb-6'>
            <SelectVersion initialValue={platformVersion?.version?.id} onChange={changeVersion} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
